import type { BagEvent, PairEvent, SpinnerEvent } from '../probabilityEvents.ts';
import type { CounterColour } from '../../../MathFactsGenerator/Constants.ts';

/**
 * English probability prompts (ADR 0108 §2). One line each, no story.
 *
 * Each colour has a letter printed on its counters and a word for the key and
 * the prompts. English reads R, B, G, Y.
 */
const COLOURS: Record<CounterColour, { letter: string; word: string }> = {
    R: { letter: 'R', word: 'red' },
    B: { letter: 'B', word: 'blue' },
    G: { letter: 'G', word: 'green' },
    Y: { letter: 'Y', word: 'yellow' },
};

/** "a 3", "an 8". */
const withArticle = (n: number) => (n === 8 ? `an ${n}` : `a ${n}`);

function spinnerEvent(event: SpinnerEvent): string {
    switch (event.kind) {
        case 'number':
            return `getting ${withArticle(event.n)}`;
        case 'not':
            return `not getting ${withArticle(event.n)}`;
        case 'even':
            return `getting an even number`;
        case 'odd':
            return `getting an odd number`;
        case 'greater':
            return `getting a number greater than ${event.n}`;
        case 'less':
            return `getting a number less than ${event.n}`;
    }
}

function bagEvent(event: BagEvent): string {
    switch (event.kind) {
        case 'colour':
            return COLOURS[event.colour].word;
        case 'not':
            return `not ${COLOURS[event.colour].word}`;
        case 'either':
            return `${COLOURS[event.colours[0]].word} or ${COLOURS[event.colours[1]].word}`;
    }
}

function pairEvent(event: PairEvent): string {
    switch (event.kind) {
        case 'both':
            return `getting ${withArticle(event.left)} on the left and ${withArticle(event.right)} on the right`;
        case 'bothEven':
            return `getting two even numbers`;
        case 'same':
            return `getting the same number on both`;
    }
}

export const probabilityPrompts = {
    /** Printed once above the first problem of each question page. */
    instruction: () => `Write each probability as a fraction in lowest terms.`,
    colours: COLOURS,
    /** The key under a bag: "R = red, B = blue, G = green, Y = yellow". */
    counterKey: () =>
        (Object.keys(COLOURS) as CounterColour[]).map((c) => `${COLOURS[c].letter} = ${COLOURS[c].word}`).join(', '),
    oneSpinner: (event: SpinnerEvent) => `Spin once. What is the probability of ${spinnerEvent(event)}?`,
    bag: (event: BagEvent) => `Take one counter without looking. What is the probability it is ${bagEvent(event)}?`,
    twoSpinners: (event: PairEvent) => `Spin both. What is the probability of ${pairEvent(event)}?`,
    outcomes: () => `Spin both. How many different outcomes are there?`,
};
